/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/QaBulkAssignBar.tsx
 * FILE_ID: JOBHUB-QA-BULK-ASSIGN-BAR-001
 * PURPOSE: One select-then-act toolbar for areas, checklists, and checks.
 * LANDMINE: Per-verb gating. canAssign hides only the assign cluster;
 *      canOrganise gates delete/move/rename/reorder. Generate ITP only reads
 *      and stays available. Nothing here may be absolute, fixed or z-indexed.
 */
import { useRef, useState, type MouseEvent } from 'react'
import {
  CalendarDays,
  Check,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  FolderInput,
  FolderPlus,
  Loader2,
  Pencil,
  Trash2,
  X,
} from 'lucide-react'

import { QaAssigneeChip } from '../../qa/QaAssigneeChip'
import type { QaAssigneeCandidate } from '../../qa/qaTypes'

function IconAction({
  testId,
  label,
  title,
  disabled,
  onClick,
  children,
}: {
  testId: string
  label: string
  title: string
  disabled?: boolean
  onClick: (event: MouseEvent<HTMLButtonElement>) => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      title={title}
      aria-label={label}
      data-testid={testId}
      className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border bg-background text-foreground hover:bg-accent disabled:opacity-50"
    >
      {children}
    </button>
  )
}

/** A bare `type="date"` prints its own "yyyy-mm-dd" placeholder and eats a
 *  whole toolbar slot even when unset. The calendar icon opens the same
 *  native picker; the chosen date only takes space once it exists. */
function DueDateControl({
  scope,
  label,
  value,
  onChange,
}: {
  scope: string
  label: string
  value: string
  onChange: (next: string) => void
}) {
  const inputRef = useRef<HTMLInputElement | null>(null)

  const openPicker = () => {
    const node = inputRef.current
    if (!node) return
    // showPicker is Chrome/Edge/Safari 16+; older engines fall back to the
    // click that focusing the input already performs.
    if (typeof node.showPicker === 'function') {
      try {
        node.showPicker()
        return
      } catch {
        // Some engines throw when the input is not user-activated; fall through.
      }
    }
    node.focus()
    node.click()
  }

  return (
    <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
      <IconAction
        testId={`qa-bulk-due-open-${scope}`}
        label={`Complete by date for the selected ${label}`}
        title="Complete by"
        onClick={openPicker}
      >
        <CalendarDays className="h-3.5 w-3.5" aria-hidden />
      </IconAction>
      <input
        ref={inputRef}
        type="date"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-label={`Complete by date for the selected ${label}`}
        data-testid={`qa-bulk-due-${scope}`}
        className="sr-only"
      />
      {value && (
        <>
          <span className="text-foreground">{value}</span>
          <button
            type="button"
            onClick={() => onChange('')}
            title="Clear complete by date"
            aria-label="Clear complete by date"
            data-testid={`qa-bulk-due-clear-${scope}`}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-3 w-3" aria-hidden />
          </button>
        </>
      )}
    </span>
  )
}

export function QaBulkAssignBar({
  scope,
  label,
  candidates,
  searchCandidates,
  canAssign,
  canOrganise = false,
  count,
  allSelected,
  someSelected,
  busy = false,
  onSelectAll,
  onClear,
  onAssign,
  onMove,
  onContainedIn,
  onGenerateItp,
  itpBusy = false,
  onDelete,
  onRename,
  onMoveUp,
  onMoveDown,
  assignDisabledReason,
  selectMode,
  onToggleSelectMode,
  selectDisabled = false,
}: {
  scope: 'areas' | 'checklists' | 'items'
  label: string
  candidates: QaAssigneeCandidate[]
  searchCandidates?: (q: string) => Promise<QaAssigneeCandidate[]>
  canAssign: boolean
  canOrganise?: boolean
  count: number
  allSelected: boolean
  someSelected: boolean
  busy?: boolean
  onSelectAll: (next: boolean) => void
  onClear: () => void
  onAssign: (userId: number | null, dueDate: string, name?: string) => void
  onMove?: () => void
  onContainedIn?: (event: MouseEvent<HTMLButtonElement>) => void
  onGenerateItp?: () => void
  itpBusy?: boolean
  onDelete?: () => void
  onRename?: () => void
  onMoveUp?: () => void
  onMoveDown?: () => void
  /** Shown as a disabled Assign control so the verb is visible, not hidden. */
  assignDisabledReason?: string
  selectMode?: boolean
  onToggleSelectMode?: () => void
  selectDisabled?: boolean
}) {
  const [picked, setPicked] = useState<QaAssigneeCandidate | null>(null)
  const [dueDate, setDueDate] = useState('')
  const [openChip, setOpenChip] = useState<string | null>(null)
  const hasAction = count > 0
  const pickedId = picked?.user_id
  const confirmAssign = pickedId != null ? (
    <IconAction
      testId={`qa-bulk-confirm-assign-${scope}`}
      label="Confirm assign"
      title="Confirm assign"
      disabled={busy || Boolean(assignDisabledReason)}
      onClick={() => {
        onAssign(pickedId, dueDate, picked.name || picked.email)
        setPicked(null)
        setDueDate('')
      }}
    >
      <Check className="h-3.5 w-3.5" aria-hidden />
    </IconAction>
  ) : null
  const showCluster = canOrganise && Boolean(onRename || onMoveUp || onMoveDown || onDelete)

  return (
    <div
      className={hasAction
        ? 'flex min-w-0 flex-wrap items-center gap-1.5 rounded-lg border border-border px-2 py-1.5'
        : 'flex min-w-0 flex-wrap items-center gap-1.5'}
      data-testid={`qa-bulk-bar-${scope}`}
    >
      <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <input
          type="checkbox"
          checked={allSelected}
          ref={(node) => { if (node) node.indeterminate = someSelected }}
          onChange={(event) => onSelectAll(event.target.checked)}
          aria-label={`Select all ${label}`}
          disabled={selectDisabled}
          data-testid={`qa-bulk-select-all-${scope}`}
          className="h-3.5 w-3.5 rounded border-border"
        />
        {onToggleSelectMode ? (
          <button
            type="button"
            aria-pressed={selectMode}
            onClick={onToggleSelectMode}
            data-testid="qa-select-mode"
            title="Also make photos selectable"
            className={selectMode
              ? 'rounded px-1.5 py-0.5 bg-primary text-primary-foreground'
              : 'rounded px-1.5 py-0.5 hover:text-foreground'}
          >
            {count > 0 ? `${count} selected` : 'Select'}
          </button>
        ) : (count > 0 ? `${count} selected` : `Select ${label}`)}
      </span>

      {hasAction && (
        <>
          {canAssign && (
            <>
              <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                <QaAssigneeChip
                  chipId={`bulk:${scope}`}
                  assignedTo={picked?.user_id ?? null}
                  assignedName={picked?.name ?? null}
                  assignedEmail={picked?.email ?? null}
                  candidates={candidates}
                  searchCandidates={searchCandidates}
                  canAssign
                  openId={openChip}
                  onOpenId={setOpenChip}
                  onAssign={(userId) => {
                    if (userId == null) {
                      setPicked(null)
                      return
                    }
                    setPicked(
                      candidates.find((person) => person.user_id === userId)
                      ?? { user_id: userId, name: '', email: '' },
                    )
                  }}
                />
                <button
                  type="button"
                  aria-label="Assign"
                  className="text-[11px] text-muted-foreground hover:text-foreground"
                  onClick={() => setOpenChip(`bulk:${scope}`)}
                >
                  {picked ? picked.name || picked.email : 'Assign'}
                </button>
              </span>
              <DueDateControl
                scope={scope}
                label={label}
                value={dueDate}
                onChange={setDueDate}
              />
            </>
          )}

          {onMove && canOrganise && (
            <IconAction
              testId={`qa-bulk-move-${scope}`}
              label="Move"
              title={scope === 'areas' ? 'Move to Level' : 'Move to area'}
              disabled={busy}
              onClick={onMove}
            >
              <FolderInput className="h-3.5 w-3.5" aria-hidden />
            </IconAction>
          )}
          {onContainedIn && scope === 'areas' && canOrganise && (
            <IconAction
              testId="qa-bulk-contained-in-areas"
              label="Belongs to Level:"
              title="Belongs to Level:"
              disabled={busy}
              onClick={onContainedIn}
            >
              <FolderPlus className="h-3.5 w-3.5" aria-hidden />
            </IconAction>
          )}

          {onGenerateItp && (
            <IconAction
              testId={`qa-bulk-itp-${scope}`}
              label="Generate ITP"
              title="Generate ITP"
              disabled={itpBusy}
              onClick={onGenerateItp}
            >
              {itpBusy
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                : <ClipboardList className="h-3.5 w-3.5" aria-hidden />}
            </IconAction>
          )}

          {/* Rename, reorder and delete act on the same row, so they stay one
              cluster: wrapping Delete away from Up/Down puts a destructive
              button somewhere the user is not looking for it. */}
          {showCluster ? (
            <span className="flex shrink-0 items-center gap-1.5">
              {confirmAssign}
              {onRename && (
                <IconAction
                  testId={`qa-bulk-rename-${scope}`}
                  label="Rename"
                  title="Rename"
                  disabled={count !== 1}
                  onClick={onRename}
                >
                  <Pencil className="h-3.5 w-3.5" aria-hidden />
                </IconAction>
              )}

              {onMoveUp && (
                <IconAction
                  testId={`qa-bulk-up-${scope}`}
                  label="Move up"
                  title="Move up"
                  disabled={count !== 1 || busy}
                  onClick={onMoveUp}
                >
                  <ChevronUp className="h-3.5 w-3.5" aria-hidden />
                </IconAction>
              )}

              {onMoveDown && (
                <IconAction
                  testId={`qa-bulk-down-${scope}`}
                  label="Move down"
                  title="Move down"
                  disabled={count !== 1 || busy}
                  onClick={onMoveDown}
                >
                  <ChevronDown className="h-3.5 w-3.5" aria-hidden />
                </IconAction>
              )}

              {onDelete && (
                <IconAction
                  testId={`qa-bulk-delete-${scope}`}
                  label="Delete"
                  title="Delete"
                  disabled={busy}
                  onClick={onDelete}
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden />
                </IconAction>
              )}
            </span>
          ) : confirmAssign}

          <button
            type="button"
            onClick={onClear}
            title="Clear selection"
            aria-label="Clear selection"
            data-testid={`qa-bulk-clear-${scope}`}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        </>
      )}
    </div>
  )
}
